import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { createCallsApi } from "../calls/calls-api";

const DEFAULT_DURATION_MINUTES = 30;

// "YYYY-MM-DD" for the date input's default value.
const getTodayDate = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
};

// "HH:MM" for the time input's default value.
const formatTime = (d) => `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;

const getCurrentTime = () => formatTime(new Date());

// Given "HH:MM", return "HH:MM" shifted forward by DEFAULT_DURATION_MINUTES,
// wrapping within the same day (caps at 23:59 rather than rolling to the next day).
const addDefaultDuration = (time) => {
  if (!time) return "";
  const [h, m] = time.split(":").map(Number);
  const total = Math.min(h * 60 + m + DEFAULT_DURATION_MINUTES, 23 * 60 + 59);
  const d = new Date();
  d.setHours(Math.floor(total / 60), total % 60, 0, 0);
  return formatTime(d);
};

// Minimal Create Call popup — shared across Account view, Lead view, and
// anywhere else that needs to quick-create a call linked to a record.
// `linkTo` tells the popup (and the "More details" full-page handoff) which
// record to attach the new call to: { type: "Account" | "Lead", id, label }.
const CreateCallModal = ({ isOpen, onClose, onCreated, linkTo, notify }) => {
  const navigate = useNavigate();

  const [saving, setSaving] = useState(false);
  const [title, setTitle] = useState("");
  const [relatedTo, setRelatedTo] = useState("");
  const [date, setDate] = useState("");
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [callType, setCallType] = useState("");
  const [errors, setErrors] = useState({});

  // Default Date to today, Start Time to now, End Time to 30 minutes after
  // Start Time, and Related To to the linked record's name — all whenever
  // the popup opens. Related To stays editable as free text from there;
  // it's just a display label (crm_calls.related_to), unrelated to which
  // record the call is actually linked to (call_for/call_for_id).
  useEffect(() => {
    if (!isOpen) return;
    const now = getCurrentTime();
    setDate(getTodayDate());
    setStartTime(now);
    setEndTime(addDefaultDuration(now));
    setRelatedTo(linkTo?.label ? `${linkTo.type}: ${linkTo.label}` : "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const resetForm = () => {
    setTitle("");
    setRelatedTo("");
    setDate("");
    setStartTime("");
    setEndTime("");
    setCallType("");
    setErrors({});
  };

  const handleStartTimeChange = (value) => {
    setStartTime(value);
    // Keep End Time 30 minutes ahead automatically; once the user has
    // started shifting Start Time this still re-syncs End Time relative to
    // it, matching the "30 min default" behavior requested for this popup.
    setEndTime(addDefaultDuration(value));
  };

  const handleClose = () => {
    onClose();
    resetForm();
  };

  const handleSubmit = async () => {
    const nextErrors = {};
    if (!title.trim()) nextErrors.title = "Title is required.";
    if (!date) nextErrors.date = "Date is required.";
    if (!startTime) nextErrors.start_time = "Start time is required.";
    if (!endTime) nextErrors.end_time = "End time is required.";
    if (date && startTime && endTime && `${date} ${startTime}` >= `${date} ${endTime}`) {
      nextErrors.end_time = "End time must be after start time.";
    }
    if (!linkTo?.id) {
      notify?.(`Unable to link this call to a ${linkTo?.type?.toLowerCase() || "record"}. Please reopen this page and try again.`, false);
      return;
    }
    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      return;
    }

    setSaving(true);
    try {
      const formattedStartTime = startTime.length === 5 ? `${startTime}:00` : startTime;
      const formattedEndTime = endTime.length === 5 ? `${endTime}:00` : endTime;

      const payload = {
        title,
        start_time: `${date} ${formattedStartTime}`,
        end_time: `${date} ${formattedEndTime}`,
        subject: "",
        related_to: relatedTo,
        call_type: callType,
        call_purpose: "",
        call_for: linkTo?.type,
        call_for_id: linkTo?.id,
        remarks: "",
        participants: [],
        reminders: [],
        follow_up: false,
      };
      const response = await createCallsApi(payload);
      if (response?.data?.success) {
        notify?.(response.data.message || "Call created successfully", true);
        onClose();
        resetForm();
        onCreated?.(response);
      } else {
        notify?.(response?.data?.message || "Failed to create call", false);
      }
    } catch (error) {
      const apiError = error?.response?.data;
      let errorMsg = apiError?.message || "Failed to create call";
      if (apiError?.error && typeof apiError.error === "object") {
        const firstField = Object.keys(apiError.error)[0];
        if (firstField && apiError.error[firstField]?.[0]) {
          errorMsg = apiError.error[firstField][0];
        }
      }
      notify?.(errorMsg, false);
    } finally {
      setSaving(false);
    }
  };

  const handleMoreDetails = () => {
    onClose();
    navigate("/calls/create", {
      state: {
        callFrom: linkTo?.type,
        moduleDetail: { value: linkTo?.id, label: linkTo?.label },
        prefill: {
          title,
          date,
          start_time: startTime,
          end_time: endTime,
          call_type: callType,
        },
      },
    });
  };

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100vw",
        height: "100vh",
        backgroundColor: "rgba(0,0,0,0.5)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 1000,
      }}
      onClick={handleClose}
    >
      <div
        style={{
          backgroundColor: "#fff",
          borderRadius: "16px",
          width: "480px",
          maxWidth: "92vw",
          maxHeight: "85vh",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "16px 24px",
            borderBottom: "1px solid #ebebeb",
          }}
        >
          <div className="fs-16 fw-600 text-dkblue">Create Call</div>
          <span
            style={{ cursor: "pointer", fontSize: "18px", color: "#8790A1" }}
            onClick={handleClose}
          >
            ✕
          </span>
        </div>

        <div style={{ padding: "24px", overflowY: "auto", display: "flex", flexDirection: "column", gap: "16px" }}>
          <div>
            <div className="fs-13 fw-500" style={{ marginBottom: "6px", color: "#21272c" }}>
              Title<span style={{ color: "#ff4444" }}>*</span>
            </div>
            <input
              type="text"
              className="form-control"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Enter call title..."
            />
            {errors.title && <div className="fs-12" style={{ color: "#ff4444", marginTop: "4px" }}>{errors.title}</div>}
          </div>

          <div>
            <div className="fs-13 fw-500" style={{ marginBottom: "6px", color: "#21272c" }}>
              Related To
            </div>
            <input
              type="text"
              className="form-control"
              value={relatedTo}
              onChange={(e) => setRelatedTo(e.target.value)}
              placeholder="Enter related to..."
            />
          </div>

          <div style={{ display: "flex", gap: "12px" }}>
            <div style={{ flex: 1 }}>
              <div className="fs-13 fw-500" style={{ marginBottom: "6px", color: "#21272c" }}>
                Date<span style={{ color: "#ff4444" }}>*</span>
              </div>
              <input
                type="date"
                className="form-control"
                value={date}
                onChange={(e) => setDate(e.target.value)}
              />
              {errors.date && <div className="fs-12" style={{ color: "#ff4444", marginTop: "4px" }}>{errors.date}</div>}
            </div>
            <div style={{ flex: 1 }}>
              <div className="fs-13 fw-500" style={{ marginBottom: "6px", color: "#21272c" }}>
                Start Time<span style={{ color: "#ff4444" }}>*</span>
              </div>
              <input
                type="time"
                className="form-control"
                value={startTime}
                onChange={(e) => handleStartTimeChange(e.target.value)}
              />
              {errors.start_time && <div className="fs-12" style={{ color: "#ff4444", marginTop: "4px" }}>{errors.start_time}</div>}
            </div>
            <div style={{ flex: 1 }}>
              <div className="fs-13 fw-500" style={{ marginBottom: "6px", color: "#21272c" }}>
                End Time<span style={{ color: "#ff4444" }}>*</span>
              </div>
              <input
                type="time"
                className="form-control"
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
              />
              {errors.end_time && <div className="fs-12" style={{ color: "#ff4444", marginTop: "4px" }}>{errors.end_time}</div>}
            </div>
          </div>

          <div>
            <div className="fs-13 fw-500" style={{ marginBottom: "6px", color: "#21272c" }}>
              Call Type
            </div>
            <select className="form-control form-select" value={callType} onChange={(e) => setCallType(e.target.value)}>
              <option value="">Select Call Type</option>
              <option value="Inbound Call">Inbound Call</option>
              <option value="Outbound Call">Outbound Call</option>
              <option value="Follow-up Call">Follow-up Call</option>
              <option value="Sales Call">Sales Call</option>
              <option value="Support Call">Support Call</option>
              <option value="Service Call">Service Call</option>
            </select>
          </div>
        </div>

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "12px", padding: "16px 24px", borderTop: "1px solid #ebebeb" }}>
          <span
            style={{ color: "#3D4AF5", fontWeight: 600, fontSize: "14px", cursor: "pointer" }}
            onClick={handleMoreDetails}
          >
            More details →
          </span>
          <div style={{ display: "flex", gap: "12px" }}>
            <button
              style={{ backgroundColor: "#fff", color: "#21272c", border: "1px solid #d4dde7", borderRadius: "100px", padding: "8px 24px", fontWeight: 600, fontSize: "14px", cursor: "pointer" }}
              onClick={handleClose}
            >
              Cancel
            </button>
            <button
              style={{ backgroundColor: "#6B46FE", color: "#fff", border: "none", borderRadius: "100px", padding: "8px 24px", fontWeight: 600, fontSize: "14px", cursor: saving ? "not-allowed" : "pointer", opacity: saving ? 0.7 : 1 }}
              onClick={handleSubmit}
              disabled={saving}
            >
              {saving ? "Saving..." : "Save"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateCallModal;
